import React, { useState, useEffect } from 'react';
import './styles.css';
import {
  useGetAvailableSeasonRequest,
  useGetAvailableGendersRequest,
  useGetAvailableLinesRequest,
  useGetAvailableMacroFamiliesRequest,
  useGetAvailableMicroFamiliesRequest
} from '../../../../hooks/useApi';

const FilterSelectionModal = ({posId, filterType, selectedValues, selectedFilters, onClose, onSelect}) =>
{
  const [searchQuery, setSearchQuery] = useState('');
  const [localSelected, setLocalSelected] = useState(selectedValues || []);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [{data: seasonsData}, getSeasons] = useGetAvailableSeasonRequest(posId);
  const [{data: gendersData}, getGenders] = useGetAvailableGendersRequest(posId);
  const [{data: linesData}, getLines] = useGetAvailableLinesRequest(posId);
  const [{data: macroData}, getMacroFamilies] = useGetAvailableMacroFamiliesRequest(posId);
  const [{data: microData}, getMicroFamilies] = useGetAvailableMicroFamiliesRequest(posId);

  useEffect(() =>
  {
    setLoading(true);

    if (filterType === 'season')
    {
      getSeasons();
    } else if (filterType === 'gender')
    {
      getGenders();
    } else if (filterType === 'line')
    {
      getLines({
        bodyParams: {
          genders: selectedFilters.genders.length > 0 ? selectedFilters.genders : []
        }
      });
    } else if (filterType === 'macroFamily')
    {
      getMacroFamilies({
        bodyParams: {
          lines: selectedFilters.lines.length > 0 ? selectedFilters.lines : []
        }
      });
    } else if (filterType === 'microFamily')
    {
      getMicroFamilies({
        bodyParams: {
          macroFamilies: selectedFilters.macroFamilies.length > 0 ? selectedFilters.macroFamilies : []
        }
      });
    }
  }, [filterType]);

  useEffect(() =>
  {
    if (filterType === 'season' && seasonsData)
    {
      setOptions(seasonsData.map(s => ({id: s.seasonId, label: s.seasonDesc})));
      setLoading(false);
    } else if (filterType === 'gender' && gendersData)
    {
      setOptions(gendersData.map(g => ({id: g.genderId, label: g.genderDesc})));
      setLoading(false);
    } else if (filterType === 'line' && linesData)
    {
      setOptions(linesData.map(l => ({id: l.lineId, label: l.lineDesc})));
      setLoading(false);
    } else if (filterType === 'macroFamily' && macroData)
    {
      setOptions(macroData.map(m => ({id: m.macroFamilyId, label: m.macroFamilyDesc})));
      setLoading(false);
    } else if (filterType === 'microFamily' && microData)
    {
      setOptions(microData.map(m => ({id: m.microFamilyId, label: m.microFamilyDesc})));
      setLoading(false);
    }
  }, [seasonsData, gendersData, linesData, macroData, microData]);

  const handleToggle = (id) =>
  {
    setLocalSelected(prev =>
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () =>
  {
    const allIds = filteredOptions.map(opt => opt.id);
    setLocalSelected(allIds);
  };

  const handleClear = () =>
  {
    setLocalSelected([]);
  };

  const handleAddSelected = () =>
  {
    const filterKey =
      filterType === 'season' ? 'seasons' :
        filterType === 'gender' ? 'genders' :
          filterType === 'line' ? 'lines' :
            filterType === 'macroFamily' ? 'macroFamilies' :
              filterType === 'microFamily' ? 'microFamilies' :
                'seasons';

    // Create labels map
    const labels = {};
    localSelected.forEach(id => {
      const option = options.find(opt => opt.id === id);
      if (option) {
        labels[id] = option.label;
      }
    });

    onSelect(filterKey, localSelected, labels);
  };

  const filteredOptions = options.filter(opt =>
    opt.label && opt.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTitle = () =>
  {
    switch (filterType)
    {
      case 'season':
        return 'Select Season';
      case 'gender':
        return 'Select Gender';
      case 'line':
        return 'Select Line';
      case 'macroFamily':
        return 'Select Macro Family';
      case 'microFamily':
        return 'Select Micro Family';
      default:
        return 'Select Filter';
    }
  };

  return (
    <div className="filter-modal-overlay" onClick={onClose}>
      <div className="filter-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="filter-modal-header">
          <button className="back-button" onClick={onClose}>‹</button>
          <h2 className="modal-title">{getTitle()}</h2>
          <div style={{width: '32px'}}></div>
        </div>

        <div className="filter-modal-body">
          <div className="search-box">
            <input
              type="text"
              className="search-input"
              placeholder={`Search ${filterType}s...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="filter-actions">
            <button className="filter-action-btn" onClick={handleSelectAll}>
              <input
                type="checkbox"
                checked={localSelected.length === filteredOptions.length && filteredOptions.length > 0}
                readOnly
              />
              <span>Select All</span>
            </button>
            <button className="filter-action-link" onClick={handleClear}>
              Clear
            </button>
          </div>

          <div className="filter-options-list">
            {loading ? (
              <div className="filter-loading">Loading...</div>
            ) : filteredOptions.length === 0 ? (
              <div className="filter-empty">No options available</div>
            ) : (
              filteredOptions.map(option => (
                <label key={option.id} className="filter-option-item">
                  <input
                    type="checkbox"
                    checked={localSelected.includes(option.id)}
                    onChange={() => handleToggle(option.id)}
                  />
                  <span className="filter-option-label">{option.label}</span>
                </label>
              ))
            )}
          </div>
        </div>

        <div className="filter-modal-footer">
          <button
            className="btn btn-primary btn-block"
            onClick={handleAddSelected}
            disabled={localSelected.length === 0}
          >
            Add Selected
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterSelectionModal;
